import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Check, ExternalLink, ArrowRight } from 'lucide-react';

import FormField from './components/FormField';
import RoleCombobox from './components/RoleCombobox';
import SelfSetupCard from './components/SelfSetupCard';
import TaskItem from './components/TaskItem';
import { useAddEmployee } from './useAddEmployee';
import { SETUP_TASKS } from './constants';
import {
  BRAND, BRAND_DARK, BRAND_SOFT,
  TEXT_INK, TEXT_SECONDARY, BG_CARD, BORDER,
  SUCCESS_TEXT, SUCCESS_SOFT,
} from './styles';
import type { EmployeeForm, ValidationErrors, Step } from './types';

const INITIAL_FORM: EmployeeForm = {
  firstName: '',
  middleInitial: '',
  lastName: '',
  email: '',
  mobile: '',
  role: '',
  hireDate: '',
  selfSetup: true,
};

export default function AddEmployee() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState<EmployeeForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { submit, submitting, submitError, createdEmployee, reset } = useAddEmployee();

  const update = <K extends keyof EmployeeForm>(key: K, value: EmployeeForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

   const goBack = (): void => { navigate('/payroll/employees'); };

  const validate = (): boolean => {
    const e: ValidationErrors = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.lastName.trim()) e.lastName = 'Last name is required.';
    if (form.selfSetup && !form.email.trim()) e.email = 'Email is required to send the invite.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate()) return;
    const result = await submit(form);
    if (result) setStep('success');
  };

  const resetForm = (): void => {
    setForm(INITIAL_FORM);
    setErrors({});
    reset();
    setStep('form');
  };

  const successName = (createdEmployee && createdEmployee.first_name) || form.firstName.trim() || 'your new employee';
  const successId = createdEmployee && createdEmployee.id;

  return (
    <div style={{ background: BG_CARD, minHeight: '100vh', width: '100%', fontFamily: 'inherit' }}>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 28px', borderBottom: '0.5px solid ' + BORDER,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span onClick={goBack} style={{
            width: 34, height: 34, display: 'grid', placeItems: 'center',
            borderRadius: 8, color: TEXT_SECONDARY, cursor: 'pointer',
          }}><ArrowLeft size={18} /></span>
          <h1 style={{
            fontSize: 22, fontWeight: 700, color: TEXT_INK, margin: 0,
            letterSpacing: '-0.01em',
          }}>Add an employee</h1>
        </div>
        <span onClick={goBack} style={{
          width: 34, height: 34, display: 'grid', placeItems: 'center',
          borderRadius: 8, color: TEXT_SECONDARY, cursor: 'pointer',
        }}><X size={18} /></span>
      </div>

      {step === 'form' && (
        <div style={{ padding: '28px 28px 60px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 640 }}>

            <h2 style={{
              fontSize: 24, fontWeight: 700, color: TEXT_INK,
              margin: '0 0 8px', letterSpacing: '-0.01em',
            }}>Who's your new team member?</h2>
            <p style={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.6, margin: '0 0 8px' }}>
              Add your employee to get them paid. Add their email (mobile coming soon) to give them access to their employee portal in Novala, where they can view pay, T4s, and documents.
            </p>
            <a onClick={() => navigate('/employee-portal')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 13, fontWeight: 600, color: BRAND_DARK, cursor: 'pointer',
            }}>
              Learn how the employee portal works<ExternalLink size={13} />
            </a>

            <div style={{
              marginTop: 28, background: BG_CARD,
              border: '0.5px solid ' + BORDER, borderRadius: 12, padding: 24,
            }}>

              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 1fr',
                gap: 12, marginBottom: 16,
              }}>
                <FormField label="First name" required value={form.firstName} onChange={(v) => update('firstName', v)} error={errors.firstName} placeholder="Claire" />
                <FormField label="M.I." value={form.middleInitial} onChange={(v) => update('middleInitial', v.slice(0, 1))} placeholder="" maxLength={1} />
                <FormField label="Last name" required value={form.lastName} onChange={(v) => update('lastName', v)} error={errors.lastName} placeholder="Kemanzi" />
              </div>

              <div style={{ marginBottom: 16 }}>
                <FormField label="Email (for the invite)" required={form.selfSetup} value={form.email} onChange={(v) => update('email', v)} error={errors.email} placeholder="claire@brightcare.ca" type="email" helper="We email the setup invite here. Add a mobile later to text it too." />
              </div>

              <div style={{ marginBottom: 16 }}>
                <FormField label="Mobile (optional)" value={form.mobile} onChange={(v) => update('mobile', v)} placeholder="780-555-0123" type="tel" helper="Used for text invites once SMS is connected." />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_INK, marginBottom: 6 }}>Role</div>
                <RoleCombobox value={form.role} onChange={(v) => update('role', v)} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <FormField label="Hire date" value={form.hireDate} onChange={(v) => update('hireDate', v)} type="date" />
              </div>

              <div style={{ height: '0.5px', background: BORDER, margin: '0 -24px 24px' }} />

              <h3 style={{ fontSize: 16, fontWeight: 700, color: TEXT_INK, margin: '0 0 5px' }}>Save time: let them set up their own details</h3>
              <p style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.55, margin: '0 0 14px' }}>
                They can enter personal, tax, and direct deposit info themselves, securely.
              </p>

              <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                <SelfSetupCard selected={form.selfSetup} onClick={() => update('selfSetup', true)} title="Employee self setup" body="We email them a secure link to complete their own personal, tax, and direct deposit info." />
                <SelfSetupCard selected={!form.selfSetup} onClick={() => update('selfSetup', false)} title="I'll enter everything myself" body="You fill in all of their details now. You can still invite them to the portal later." />
              </div>

              {form.selfSetup && (
                <div style={{
                  background: BRAND_SOFT, border: '0.5px solid ' + BRAND,
                  borderRadius: 10, padding: 16, marginBottom: 8,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: BRAND_DARK, marginBottom: 8 }}>
                    What {form.firstName.trim() || 'they'} will be asked to complete
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
                    {SETUP_TASKS.map((item) => (
                      <li key={item} style={{
                        fontSize: 13, color: BRAND_DARK,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <Check size={14} />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {submitError && (
                <div style={{
                  padding: 12, background: '#FEE2E2',
                  border: '0.5px solid #F87171', borderRadius: 8,
                  color: '#991B1B', fontSize: 13, marginTop: 16,
                }}>
                  {submitError}
                </div>
              )}

              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSubmit} disabled={submitting} style={{
                  background: BRAND, color: 'white',
                  fontSize: 14, fontWeight: 600, padding: '11px 22px',
                  border: 'none', borderRadius: 9,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  fontFamily: 'inherit', display: 'inline-flex',
                  alignItems: 'center', gap: 7,
                }}>
                  {submitting ? 'Adding...' : 'Add employee'}<ArrowRight size={15} />
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div style={{ padding: '28px 28px 60px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 720, textAlign: 'center' }}>

            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: SUCCESS_SOFT, margin: '12px auto 18px',
              display: 'grid', placeItems: 'center',
            }}>
              <Check size={32} style={{ color: SUCCESS_TEXT }} />
            </div>
            <h2 style={{
              fontSize: 26, fontWeight: 700, color: TEXT_INK,
              margin: '0 0 8px', letterSpacing: '-0.02em',
            }}>You added {successName}!</h2>
            <p style={{ fontSize: 14, color: TEXT_SECONDARY, margin: '0 0 28px' }}>
              Here's what's next to get them ready for payroll.
            </p>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
              textAlign: 'left', marginBottom: 28,
            }}>
              <div style={{
                background: BG_CARD, border: '0.5px solid ' + BORDER,
                borderRadius: 12, padding: 20,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY,
                  marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>For you</div>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
                  <TaskItem text={'Complete ' + successName + "'s payroll info"} />
                  <TaskItem text="Add role and any credentials or certifications, with expiry dates" />
                  <TaskItem text="Share documents in their portal" />
                </ul>
              </div>
              <div style={{
                background: BG_CARD, border: '0.5px solid ' + BORDER,
                borderRadius: 12, padding: 20,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY,
                  marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>For {successName}</div>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
                  <TaskItem text="Download the Novala app to view pay, T4s, and documents" />
                </ul>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              <button onClick={resetForm} style={{
                background: BG_CARD, color: BRAND_DARK,
                fontSize: 14, fontWeight: 600, padding: '10px 18px',
                border: '0.5px solid ' + BRAND, borderRadius: 9,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Add another employee
              </button>
              <button onClick={() => successId ? navigate('/payroll/employees/' + successId) : navigate('/payroll/employees')} style={{
                background: BRAND, color: 'white',
                fontSize: 14, fontWeight: 600, padding: '11px 22px',
                border: 'none', borderRadius: 9,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Finish payroll info
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}